import { ChangeDetectionStrategy, Component, EventEmitter, Inject, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ConfigData, CuttingService, IColor, IConfig } from '@shared/services/cutting.service';
import { ValidatorPatterns } from '@shared/validatorpatterns';

@Component({
	selector: 'app-create-update-color-dialog',
	templateUrl: './create-update-color.html',
	styleUrls: ['./create-update-color.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class CreateUpdateColorDialogComponent implements OnInit {
	@Output() saved = new EventEmitter<void>();
	form: FormGroup;
	color: IColor;
	httpError = '';
	loading = false;

	constructor(
		@Inject(MAT_DIALOG_DATA) public data: ConfigData,
		public dialogRef: MatDialogRef<CreateUpdateColorDialogComponent>,
		private cuttingService: CuttingService,
		private fb: FormBuilder,
	) {
		this.color = {
			colors: null,
		};

		if (data.editColor) {
			this.color = { ...data.editColor };
		}
	}

	getColor(): IColor {
		return this.data.editColor;
	}

	getOtherColors(): IColor[] {
		return this.data.otherColors;
	}

	getConfig(): string {
		return this.data.self;
	}

	ngOnInit(): void {
		this.initializeForm();
	}

	private initializeForm(): void {
		this.form = this.fb.group({
			colors: [this.color.colors, [Validators.maxLength(250), Validators.pattern(ValidatorPatterns.ColorPattern)]],
		});

		this.subscribeToFormChanges();
	}

	private subscribeToFormChanges(): void {
		this.form.valueChanges.subscribe((value) => {
			Object.keys(value).forEach((key) => {
				if (key in this.color) {
					this.color[key] = value[key];
				}
			});
		});
	}

	get controls() {
		return this.form.controls;
	}

	onCancel(): void {
		this.dialogRef.close();
	}

	async onSave() {
		this.httpError = '';
		if (this.form.invalid || this.loading) return;
		this.loading = true;

		const createMachineConfig = (machine) => ({
			self: machine.self,
			divisionType: machine.divisionType,
		});

		const colors = [this.color, ...this.getOtherColors()].map((color) => color.colors.split(', ').reverse());

		const config: IConfig = {
			self: this.getConfig(),
			prohibitedColorPairing: colors,
			allowFrameAndSashInSameBasket: this.data.configData.allowFrameAndSashInSameBasket,
			allowMixedCrossSectionInSameBasket: this.data.configData.allowMixedCrossSectionInSameBasket,
			generateRodsForWaste: this.data.configData.generateRodsForWaste,
			extraRodsPercentage: this.data.configData.extraRodsPercentage,
			machines: this.data.configData.machines.map(createMachineConfig),
		};

		const saveObservable = this.color ? this.cuttingService.updateConfig(config) : this.cuttingService.createConfig(config);

		saveObservable.subscribe({
			next: () => {
				this.saved.emit();
				this.dialogRef.close();
			},
			error: ({ status }) => {
				this.httpError = `${status}`;
				this.loading = false;
			},
		});
	}
}
